import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { FiltrosAPI } from "../../../types/api";
import type { DashboardFilters } from "../components/DashboardFilters";

export const SLA_DEFAULT_VALUES = [
  "pausado",
  "aguardando_fila",
  "concluido_prazo",
  "concluido_atraso",
];

interface FilterOptionsData {
  severidades: Array<{ id: number }>;
  status: Array<{ id: number; nome: string }>;
}

export const isStatusUnmarkedByDefault = (nome: string) =>
  /cancelad|aguardando\s*implanta|homologa/i.test(nome);

const defaultStatusIds = (status: Array<{ id: number; nome: string }>) =>
  status
    .filter((s) => !isStatusUnmarkedByDefault(s.nome))
    .map((s) => s.id.toString());

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

export const useDashboardFilters = (filterOptions?: FilterOptionsData) => {
  const [filters, setFilters] = useState<DashboardFilters>({
    texto: "",
    severidade: [],
    categoriaGrupo: "suporte",
    status: [],
    slaStatus: [...SLA_DEFAULT_VALUES],
    mesApuracao: getCurrentMonth(),
    produtos: [],
  });

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!filterOptions) return;
    if (
      filterOptions.severidades.length === 0 &&
      filterOptions.status.length === 0
    )
      return;

    setFilters((prev) => ({
      ...prev,
      severidade: filterOptions.severidades.map((s) => s.id.toString()),
      status: defaultStatusIds(filterOptions.status),
    }));
    initializedRef.current = true;
  }, [filterOptions]);

  const formatMonthYear = (yearMonth: string) => {
    const [year, month] = yearMonth.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date
      .toLocaleDateString("pt-BR", { month: "short", year: "numeric" })
      .replace(".", "");
  };

  const generateMonthOptions = useCallback(() => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    options.push({ value: "", label: "Todos os meses" });

    for (let i = 0; i < 24; i++) {
      const date = new Date(currentYear, currentDate.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;
      options.push({
        value: yearMonth,
        label: formatMonthYear(yearMonth),
      });
    }

    return options;
  }, []);

  const getAPIFilters = useMemo((): FiltrosAPI => {
    const apiFilters: FiltrosAPI = {};

    // O texto da busca não vai para a API: o quadro filtra na tela
    // (filtrarQuadroPorBusca), senão cada tecla seria uma consulta nova.
    if (filters.severidade.length > 0)
      apiFilters.severidade = filters.severidade.map(Number);
    if (filters.categoriaGrupo === "novas_funcionalidades") {
      apiFilters.categoria = 2;
    }
    if (filters.status.length > 0)
      apiFilters.status = filters.status.map(Number);

    if (filters.mesApuracao && filters.mesApuracao !== "") {
      const [year, month] = filters.mesApuracao.split("-");
      apiFilters.ano = parseInt(year);
      apiFilters.mes = parseInt(month);
    }

    return apiFilters;
  }, [filters]);

  const updateFilters = useCallback(
    (newFilters: DashboardFilters | ((prev: DashboardFilters) => DashboardFilters)) => {
      setFilters(newFilters);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setFilters({
      texto: "",
      severidade: filterOptions?.severidades.map((s) => s.id.toString()) ?? [],
      categoriaGrupo: "suporte",
      status: filterOptions ? defaultStatusIds(filterOptions.status) : [],
      slaStatus: [...SLA_DEFAULT_VALUES],
      mesApuracao: getCurrentMonth(),
      produtos: [],
    });
  }, [filterOptions]);

  const totalSeveridades = filterOptions?.severidades.length ?? 0;
  const statusDefaults = filterOptions
    ? defaultStatusIds(filterOptions.status)
    : [];

  const hasActiveFilters = useMemo(() => {
    const severidadeRestrictive =
      totalSeveridades > 0 && filters.severidade.length !== totalSeveridades;
    const statusIsDefault =
      filters.status.length === statusDefaults.length &&
      statusDefaults.every((id) => filters.status.includes(id));
    const statusRestrictive = statusDefaults.length > 0 && !statusIsDefault;
    const slaRestrictive = filters.slaStatus.length !== SLA_DEFAULT_VALUES.length;

    return (
      filters.texto !== "" ||
      severidadeRestrictive ||
      statusRestrictive ||
      slaRestrictive ||
      filters.categoriaGrupo !== "suporte" ||
      filters.mesApuracao !== getCurrentMonth() ||
      filters.produtos.length > 0
    );
  }, [filters, totalSeveridades, statusDefaults]);

  return {
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    getAPIFilters,
    mesApuracaoOptions: generateMonthOptions(),
  };
};
